import { useContext, useEffect, useMemo, useState } from "react";

import { ApiHelper as ToolkitApiHelper, USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { _sessionManager, ApiHelper, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getUnitOfMeasureAbbreviation } from "components/Budget/BudgetService";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import dayjs from "dayjs";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatMonthName, formatProducerName } from "utility/formatters";
import { v4 } from "uuid";

import { normalizeAgentTokens } from "./AgentAutoComplete";
import { isFieldDisabled, SALE_CATEGORIES, SALE_CATEGORY_CASH, SECURITY_TYPE_FUTURES } from "./SalesHelper";

// ---
export const SalesEditDialog = ({
  // Agent action: Override producer_token. Permissions enforced by API.
  producer_token,
  showProducerSelect = false,

  onChange,
  onClose,
  onDelete,
  sale,
  crop_year,
}) => {
  const { globalState } = useContext(AppContext);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { actions: formStatusActions } = useFormStatus();
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState(
    sale || {
      is_active: true,
      crop_year: crop_year,
      category: SALE_CATEGORY_CASH,
    }
  );
  const effectiveProducerToken = state?.producer_token || producer_token;

  useEffect(() => {
    setState({
      ...state,
      crop_year: crop_year,
    });
  }, [crop_year]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // ---

  const onSubmit = (e) => {
    if (!validateForm()) {
      return;
    }
    if (!state?.id) {
      state.producer_token = state.producer_token || producer_token;

      state.id = v4();
      state.crop_year = crop_year;
      state.is_active = true;
      state.trade_date = state.trade_date || new Date();
      state.trade_timestamp = state.trade_timestamp || new Date();
      state.revenue = state?.revenue || 0;
    }

    onChange?.(state);
    onClose?.();
  };

  useEffect(() => {
    setState(sale);
  }, [sale]);

  // ---

  const loadBuyers = async () => {
    try {
      if (!effectiveProducerToken) {
        setBuyers([]);
        return;
      }
      const _buyerFilter = {
        producer_token: effectiveProducerToken,
      };
      // This is agent function. Do not specify producer_token or you MAY get impersonation conflict.
      const _apiConfig = {
        agent_token: globalState?.agent_token,
      };

      const _buyers = await UsersApiHelper.users_search("Buyer", _buyerFilter, _apiConfig);
      const _buyerNames = _buyers.map(
        (buyer) => buyer?.financial_institution_name + " - " + buyer?.city_name + ", " + buyer?.state_name
      );
      setBuyers(_buyerNames);
    } catch (err) {
      handleError(err);
    }
  };

  const [buyers, setBuyers] = useState([]);
  const [clients, setClients] = useState([]);
  const [isCreateBuyerDialogOpen, setIsCreateBuyerDialogOpen] = useState(false);
  useEffect(() => {
    loadBuyers();
  }, [effectiveProducerToken, globalState?.agent_token, globalState?.user_role]);

  useEffect(() => {
    if (!showProducerSelect) {
      return;
    }
    const loadClients = async () => {
      try {
        const tokens = normalizeAgentTokens(globalState?.filter_agent_token);
        const baseFilter = {
          agent_token: globalState?.agent_token,
          is_active: true,
        };
        const apiClaims = globalState?.user_role !== USER_ROLE_ADMIN ? { agent_token: globalState?.agent_token } : {};
        if (!tokens.length) {
          setClients([]);
          return;
        }
        const results = await Promise.all(
          tokens.map((agentToken) =>
            UsersApiHelper.users_search("Client", { ...baseFilter, filter_agent_token: agentToken }, apiClaims)
          )
        );
        const seen = new Set();
        const distinct = [];
        for (const row of results.flat()) {
          const k = String(row?.producer_token ?? "").toLowerCase();
          if (!k || seen.has(k)) continue;
          seen.add(k);
          distinct.push(row);
        }
        setClients(distinct);
      } catch (err) {
        handleError(err);
        setClients([]);
      }
    };
    loadClients();
  }, [showProducerSelect, globalState?.filter_agent_token, globalState?.agent_token, globalState?.user_role]);

  const clientOptions = useMemo(() => {
    const list = clients || [];
    if (!state?.producer_token) return list;
    const hasCurrent = list.some(
      (c) => String(c.producer_token).toLowerCase() === String(state.producer_token).toLowerCase()
    );
    if (hasCurrent) return list;
    return [
      {
        producer_token: state.producer_token,
        first_name: state.first_name,
        middle_name: state.middle_name,
        last_name: state.last_name,
        business_name: state.business_name,
        account_number: state.account_number,
      },
      ...list,
    ];
  }, [
    clients,
    state?.producer_token,
    state?.first_name,
    state?.middle_name,
    state?.last_name,
    state?.business_name,
    state?.account_number,
  ]);

  const selectedClient = useMemo(
    () =>
      clientOptions.find(
        (c) => String(c?.producer_token).toLowerCase() === String(state?.producer_token ?? "").toLowerCase()
      ) || null,
    [clientOptions, state?.producer_token]
  );

  const handleCreateBuyer = async () => {
    if (state?.buyer_financial_institution_name && state?.buyer_city_name && state?.buyer_state_name) {
      // Update: Create new Buyer
      const _newBuyer = {
        id: v4(),
        producer_token: effectiveProducerToken,
        financial_institution_name: state?.buyer_financial_institution_name,
        city_name: state?.buyer_city_name,
        state_name: state?.buyer_state_name,
        country_code: "",
        is_active: true,
      };
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      // This is agent function. Do not specify producer_token or you MAY get impersonation conflict.
      const _apiConfig = {
        agent_token: globalState?.agent_token,
      };
      await api.callPost("marketing/Buyer", _newBuyer, _apiConfig);

      setIsCreateBuyerDialogOpen(false);
      const _name = _newBuyer?.financial_institution_name + " - " + _newBuyer?.city_name + ", " + _newBuyer?.state_name;
      handleChangeState({ target: { name: "financial_institution_name", value: _name } });
      loadBuyers();
    } else {
      enqueueSnackbar("Please ensure all required fields are filled and try again.", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  // ---

  const [futuresSymbols, setFuturesSymbols] = useState([]);

  const selectedFuturesSymbol = useMemo(
    () => futuresSymbols?.find((s) => s?.symbol == state?.symbol) || null,
    [futuresSymbols, state?.symbol]
  );

  const [unitOfMeasureAbbreviation, setUnitOfMeasureAbbreviation] = useState(null);
  useEffect(() => {
    const _loadUnitOfMeasureAbbreviation = async () => {
      try {
        const _currentYear = state?.crop_year;

        // Resolve some props from Budget
        // ANY budget for this Crop will provide the xref information.
        const _budgetFilter = {
          producer_token: effectiveProducerToken,
          commodity_name: state?.commodity_name,
          year: _currentYear,

          status: BUDGET_STATUS_ACTIVE,
          is_active: true,
        };
        // This is agent function. Do not specify producer_token or you MAY get impersonation conflict.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _budgets = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, _apiConfig);

        const _unitOfMeasureAbbreviationFilter = {};
        if (_budgets?.length) {
          _unitOfMeasureAbbreviationFilter.reinsurance_year = _currentYear;

          _unitOfMeasureAbbreviationFilter.state_name = _budgets[0]?.state_name;
          _unitOfMeasureAbbreviationFilter.county_name = _budgets[0]?.county_name;

          _unitOfMeasureAbbreviationFilter.commodity_name = _budgets[0]?.commodity_name;
          _unitOfMeasureAbbreviationFilter.type_name = _budgets[0]?.type_name;
          _unitOfMeasureAbbreviationFilter.practice_name = _budgets[0]?.practice_name;

          const _unitOfMeasureAbbreviation = await getUnitOfMeasureAbbreviation(_unitOfMeasureAbbreviationFilter);
          setUnitOfMeasureAbbreviation(_unitOfMeasureAbbreviation?.trim()?.toLowerCase());
        } else {
          setUnitOfMeasureAbbreviation(null);
        }
      } catch (err) {
        handleError(err);
      }
    };

    if (state?.commodity_name) {
      _loadUnitOfMeasureAbbreviation();
    } else {
      setUnitOfMeasureAbbreviation(null);
    }
  }, [state?.commodity_name, effectiveProducerToken]);

  useEffect(() => {
    const _loadFuturesSymbols = async () => {
      try {
        const _cropYear = crop_year;
        const _currentYear = dayjs().year();
        const _currentMonth = dayjs().month() + 1;

        const _symbolFilter = {
          commodity_name: state?.commodity_name,
          // Replace " " with ""
          security_type: SECURITY_TYPE_FUTURES,
        };
        const _futuresSymbols = await ApiHelper.callSmociObject(
          "data/DATANAC",
          "Markets",
          "Quotes",
          "SelectObject",
          _symbolFilter,
          globalState
        );

        const _orderedSymbols = _futuresSymbols
          .filter(
            (symbol) =>
              // Future year Or current year, not expired:
              symbol?.symbol_year >= _currentYear - 3
          )
          .map((symbol) => ({
            ...symbol,
            is_current_year: symbol?.symbol_year === _cropYear && symbol?.symbol_month_number >= _currentMonth,
            is_expired:
              symbol?.symbol_year < _cropYear ||
              (symbol?.symbol_year === _currentYear && symbol?.symbol_month_number < _currentMonth),
          }));
        _orderedSymbols.sort((a, b) => {
          if (a?.is_expired && !b?.is_expired) {
            return 1;
          }
          if (!a?.is_expired && b?.is_expired) {
            return -1;
          }
          if (a?.is_current_year && !b?.is_current_year) {
            return -1;
          }
          if (!a?.is_current_year && b?.is_current_year) {
            return 1;
          }
          if (a?.is_current_year && b?.is_current_year) {
            return a?.month_number - b?.month_number;
          }
          return a?.year - b?.year;
        });

        const _autocompleteSymbols = _orderedSymbols.map((symbol) => ({
          label:
            symbol?.contract_description +
            " - " +
            symbol?.symbol +
            " - " +
            formatMonthName(symbol?.symbol_month_number) +
            " " +
            symbol?.symbol_year +
            " " +
            symbol?.commodity_name,
          id: v4(),
          contract_description: symbol?.contract_description,
          contract_symbol: symbol?.contract_symbol,
          symbol: symbol?.symbol,
          month_name: formatMonthName(symbol?.symbol_month_number),
          symbol_month_number: symbol?.symbol_month_number,
          symbol_year: symbol?.symbol_year,
          commodity_name: symbol?.commodity_name,
        }));

        setFuturesSymbols(_autocompleteSymbols);
      } catch (err) {
        handleError(err);
        setFuturesSymbols([]);
      }
    };

    if (state?.commodity_name) {
      _loadFuturesSymbols();
    }
  }, [globalState?.producer_token, globalState?.agent_token, state?.commodity_name]);

  // ---

  const handleChangeStateSelect = (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    formStatusActions.setIsFormDirty(true, false);

    if (state[event?.target?.name] != value) {
      const _newState = { ...state };
      _newState[event?.target?.name] = value;

      setState(_newState);
    }
  };

  const handleChangeState = async (event, value) => {
    value = value ?? event.target.value;

    if (state[event?.target?.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);
      const _newState = { ...state };
      _newState[event?.target?.name] = value;
      setState(_newState);
    }
  };

  useEffect(() => {
    if (state?.delivery_period_start && state?.delivery_period_end) {
      const start = dayjs(state.delivery_period_start);
      const end = dayjs(state.delivery_period_end);

      if (start.year() > 999 && end.year() > 999) {
        if (start.isAfter(end)) {
          enqueueSnackbar("End date must be after start date", {
            variant: "error",
            preventDuplicate: true,
            key: "date-range-error",
          });
        }
      }
    }
  }, [state?.delivery_period_start, state?.delivery_period_end]);

  useEffect(() => {
    const _newState = { ...state };

    if (isFieldDisabled(state, "amount")) _newState.amount = null;
    if (isFieldDisabled(state, "price")) _newState.price = null;
    if (isFieldDisabled(state, "symbol")) _newState.symbol = null;
    if (isFieldDisabled(state, "basis")) _newState.basis = null;

    setState(_newState);
  }, [state?.category]);

  // ---

  const formMaxWidthShort = "800px";

  const validateForm = () => {
    const validationRules = [
      { field: "producer_token", message: "Client", condition: showProducerSelect },
      { field: "category", message: "Type" },
      { field: "commodity_name", message: "Commodity" },
      { field: "amount", message: "Quantity" },
      { field: "price", message: "Futures price", condition: !isFieldDisabled(state, "price") },
      { field: "symbol", message: "Symbol", condition: !isFieldDisabled(state, "symbol") },
      { field: "basis", message: "Basis", condition: !isFieldDisabled(state, "basis"), allowZero: true },
      { field: "trade_date", message: "Trade Date" },
      { field: "financial_institution_name", message: "Buyer" },
    ];

    const errors = [];

    for (const rule of validationRules) {
      const value = state?.[rule.field];
      const isEmpty = rule.allowZero ? value === null || value === undefined || value === "" : !value;

      if (isEmpty && (rule.condition ?? true)) {
        errors.push(rule.message);
      }
    }

    if (errors.length > 0) {
      const errorMessage = (
        <>
          The following fields are required:
          <br />
          {errors.join(", ")}
        </>
      );
      enqueueSnackbar(errorMessage, {
        variant: "error",
        preventDuplicate: true,
        key: "validation-errors",
      });
      return false;
    }

    if (state?.delivery_period_start && state?.delivery_period_end) {
      if (dayjs(state.delivery_period_start).isAfter(dayjs(state.delivery_period_end))) {
        enqueueSnackbar("End date must be after start date", {
          variant: "error",
          preventDuplicate: true,
          key: "date-range-error",
        });
        return false;
      }
    }

    return true;
  };
  return (
    <>
      <DialogTitle>{state?.financial_institution_name ? state?.financial_institution_name : "Add Sale"}</DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
          <Grid container spacing={2}>
            {showProducerSelect && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={clientOptions}
                    value={selectedClient}
                    getOptionLabel={(option) => formatProducerName(option) || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.producer_token ?? "").toLowerCase() ===
                      String(value?.producer_token ?? "").toLowerCase()
                    }
                    onChange={(event, newValue) => {
                      handleChangeState({
                        target: { name: "producer_token", value: newValue?.producer_token },
                      });
                    }}
                    autoSelect
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        name="producer_token"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(!state?.producer_token)}
                        helperText={!state?.producer_token ? "Required" : " "}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6} key={name}>
              <FormControl fullWidth>
                <Autocomplete
                  value={state?.category || null}
                  onChange={(event, newValue) => {
                    handleChangeStateSelect({ target: { name: "category", value: newValue } });
                  }}
                  autoSelect
                  autoHighlight
                  options={SALE_CATEGORIES}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type"
                      name="category"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(!state?.category)}
                      helperText={!state?.category ? "Required" : " "}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DataFormSelect
                value={state?.commodity_name || ""}
                name="commodity_name"
                isRequired={true}
                onChange={handleChangeStateSelect}
                objectNameLabel="Commodity"
                itemGetKey={(item) => item.commodity_name}
                itemGetLabel={(item) => item.commodity_name}
                doFetch={true}
                isLoading={false}
                multiple={false}
                limitTags={isSmall ? 2 : 3}
                showRequiredImmediately
                customDataLoader={() => RmaApiHelper.searchCommodity({}, globalState)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label={"Quantity " + (unitOfMeasureAbbreviation ? "(" + unitOfMeasureAbbreviation + ")" : "")}
                  name={"amount"}
                  isRequired={state?.category}
                  defaultValue={state?.amount || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  disabled={isFieldDisabled(state, "amount")}
                  maskType={MASK_NUMERIC}
                  showRequiredImmediately
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label={"Futures"}
                  name={"price"}
                  isRequired={!isFieldDisabled(state, "price")}
                  defaultValue={state?.price || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  disabled={isFieldDisabled(state, "price")}
                  maskType={MASK_NUMERIC}
                  showRequiredImmediately
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  name="symbol"
                  options={futuresSymbols || []}
                  multiple={false}
                  value={selectedFuturesSymbol || null}
                  getOptionLabel={(option) => option?.label || ""}
                  disabled={isFieldDisabled(state, "symbol")}
                  onChange={(event, value) => {
                    handleChangeState({ target: { name: "symbol", value: value?.symbol } });
                  }}
                  autoSelect
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      name="symbol"
                      {...params}
                      label="Symbol"
                      onChange={handleChangeState}
                      InputLabelProps={{ shrink: true }}
                      error={!isFieldDisabled(state, "symbol") && Boolean(!state?.symbol)}
                      helperText={!isFieldDisabled(state, "symbol") && !state?.symbol ? "Required" : " "}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Stack direction="column">
                          <Stack
                            direction="row"
                            sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
                          >
                            <Box>{option?.contract_description}</Box>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            key={key}
                            sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
                          >
                            <Box sx={{ textAlign: "left", width: "60pt" }}>{option?.contract_symbol}</Box>
                            <Box sx={{ alignContent: "center" }}>-</Box>
                            <Box sx={{ textAlign: "left", width: "60pt" }}>
                              {option?.month_name} {(String(option?.symbol_year) + "  ")?.substring(2, 4)}
                            </Box>
                          </Stack>
                        </Stack>
                      </li>
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label={"Basis"}
                  name={"basis"}
                  isRequired={!isFieldDisabled(state, "basis")}
                  defaultValue={state?.basis || "0"}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  disabled={isFieldDisabled(state, "basis")}
                  maskType={MASK_NUMERIC}
                  showRequiredImmediately
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TilleyDatePicker
                name="trade_date"
                label="Trade Date"
                value={state?.trade_date}
                onChange={handleChangeState}
                required
                showRequiredImmediately
                className="trade_date"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  name="financial_institution_name"
                  options={buyers || []}
                  value={state?.financial_institution_name || null}
                  getOptionLabel={(option) => option}
                  onChange={(event, value) => {
                    handleChangeState({ target: { name: "financial_institution_name", value } });
                  }}
                  autoSelect
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      name="financial_institution_name"
                      {...params}
                      label="Buyer"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(!state?.financial_institution_name)}
                      helperText={!state?.financial_institution_name ? "Required" : " "}
                    />
                  )}
                  PaperComponent={({ children }) => {
                    return (
                      <Paper>
                        {children}
                        <Button
                          color="primary"
                          variant="text"
                          fullWidth
                          sx={{ borderRadius: 0 }}
                          onMouseDown={() => {
                            setIsCreateBuyerDialogOpen(true);
                          }}
                        >
                          + Add New
                        </Button>
                      </Paper>
                    );
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TilleyDatePicker
                name="delivery_period_start"
                label="Delivery period start"
                value={state?.delivery_period_start}
                onChange={handleChangeState}
                className="delivery_period_start"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TilleyDatePicker
                name="delivery_period_end"
                label="Delivery period end"
                value={state?.delivery_period_end}
                onChange={handleChangeState}
                className="delivery_period_end"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="Contract #"
                  name="contract_number"
                  value={state?.contract_number || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 500 }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="Notes"
                  name="notes"
                  defaultValue={state?.notes || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  rows={4}
                  multiline
                  inputProps={{ maxLength: 500 }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={onSubmit}>
            OK
          </Button>
          {onDelete && state?.id && (
            <Button variant="contained" color="error" onClick={() => onDelete(sale)}>
              Delete
            </Button>
          )}
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>

      <Dialog open={isCreateBuyerDialogOpen}>
        <DialogTitle>Create Buyer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="Buyer Name"
                  name="buyer_financial_institution_name"
                  value={state?.buyer_financial_institution_name || ""}
                  required
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="City"
                  name="buyer_city_name"
                  value={state?.buyer_city_name || ""}
                  required
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="State"
                  name="buyer_state_name"
                  value={state?.buyer_state_name || ""}
                  required
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            <Button variant="contained" onClick={handleCreateBuyer}>
              OK
            </Button>
            <Button variant="contained" color="secondary" autoFocus onClick={() => setIsCreateBuyerDialogOpen(false)}>
              Cancel
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
